import * as React from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  alpha,
  useTheme,
} from "@mui/material";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import Card from "../../common/Card";
import CustomTextField from "../../common/CustomTextField";
import { useForm } from "react-hook-form";
import { tokens } from "../../../theme";
import { useBranding } from "../../../BrandingContext";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { validate2faForNonLoggedInUsers } from "../../../redux/slice/2FA/twoFactorSlice";
import { createPracticeAndUser } from "../../../redux/slice/practiceSlice";
import { login } from "../../../redux/slice/authSlice";
import { setSingleAlertObj, showHideLoadingDialog, SingleAlertInfo } from "../../../redux/slice/commonSlice";
import { ArrowBackRounded } from "@mui/icons-material";

type VerifyTwoFactorForm = {
  code: string;
};

export default function VerifyTwoFactor() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const branding = useBranding();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const createPracticeAndUserRequest = location.state?.createPracticeAndUserRequest;
  const twoFaMethod = location.state?.method;
  const { control, handleSubmit } = useForm<VerifyTwoFactorForm>({
    defaultValues: {
      code: "",
    },
  });

  const logoSrc =
    theme.palette.mode === "dark"
      ? branding?.logoWhite
      : branding?.logo ||
        require("../../../branding-assets/v3biomed/logo.png");

  const onSubmit = async (data: VerifyTwoFactorForm) => {
    dispatch(showHideLoadingDialog(true))
    const response = await dispatch(validate2faForNonLoggedInUsers({ otp: data.code, twoFaValue: twoFaMethod === "email" ? createPracticeAndUserRequest.contactEmail : createPracticeAndUserRequest.contactPhone }))
    if (response.payload && response.payload.code === "V3OK" && response.payload.message === "SUCCESS") {
      const creationResponse = await dispatch(createPracticeAndUser(createPracticeAndUserRequest))
      if (creationResponse.payload && creationResponse.payload.code === "V3OK" && creationResponse.payload.message === "V3OK") {
        try {
          const response = await dispatch(
            login({
              loginInfo: { userName: createPracticeAndUserRequest.contactEmail, password: createPracticeAndUserRequest.password },
              interceptorOption: { skipErrorPopup: true },
            }),
          ).unwrap();

          localStorage.setItem("login", Date.now().toString());
        } catch (e: any) {
          console.log("Unknown error. Please contact support.");
        }
      }
    } else {
      let errorObj: SingleAlertInfo = {
        message: "Verification codes do not match.",
        alertType: "error",
      };
      dispatch(setSingleAlertObj(errorObj));
    }
    dispatch(showHideLoadingDialog(false))
    // navigate("/platform_tour");
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        px: 2,
      }}
    >
      <Box>
        {/* Logo */}
        <Box display="flex" justifyContent="center" mb={3}>
          <Box
            component="img"
            src={logoSrc}
            alt="V3 Biomed"
            sx={{
              height: 64,
              maxWidth: "70%",
              objectFit: "contain",
            }}
          />
        </Box>

        <Card sx={{ width: "100%", maxWidth: 520 }}>
          <Box sx={{ position: "relative", display: "flex", justifyContent: "center", alignItems: "center", mb: 2 }}>
            <Button
              onClick={() => navigate("/setup_two_factor", { state: { createPracticeAndUserRequest } })}
              sx={{ position: "absolute", left: 0, top: -5, color: "#2dd4bf", pl: 0 }}
            >
              <ArrowBackRounded /> Back
            </Button>

            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                background: alpha("#14b8a6", 0.15),
              }}
            >
              <ShieldOutlinedIcon sx={{ color: "#2dd4bf" }} />
            </Box>
          </Box>
          <Stack spacing={2} alignItems="center" mb={4}>
            <Typography variant="h4" fontWeight={600} textAlign="center">
              Step 2: Set Up Two-Factor Authentication
            </Typography>

            <Typography
              textAlign="center"
              sx={{ color: "#9ca3af", maxWidth: 420 }}
            >
              Add an extra layer of security to your account by enabling 2FA
            </Typography>
          </Stack>

          <Box
            sx={{
              background: alpha("#14b8a6", 0.08),
              borderRadius: 2,
              px: 2,
              py: 1.5,
              mb: 3,
            }}
          >
            <Typography textAlign="center" sx={{ color: "#cbd5f5" }}>
              We've sent a 6-digit code to <b>your {twoFaMethod === "email" ? "email" : "mobile"}</b>
            </Typography>
          </Box>

          <Box mb={3}>
            <Typography fontWeight={600} mb={1}>
              Enter Verification Code
            </Typography>
            <CustomTextField
              controllerName="code"
              control={control}
              placeholder="000000"
              inputProps={{
                maxLength: 6,
                style: { textAlign: "center", letterSpacing: 4 },
              }}
              fullWidth
            />
          </Box>

          <Button
            fullWidth
            size="large"
            variant="contained"
            endIcon={<ArrowForwardRoundedIcon />}
            onClick={handleSubmit(onSubmit)}
            sx={{
              borderRadius: 1.5,
              backgroundColor:
                theme.palette.mode === "dark" ? "#169c9c" : "#c6ebeb",
              color: colors.grey[100],
              fontWeight: 500,
              textTransform: "none",
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark" ? "#169c9ce6" : "#b6e0e0",
              },
            }}
          >
            Verify & Continue
          </Button>

          <Button
            variant="text"
            fullWidth
            onClick={() => navigate("/create_account_step", { state: { createPracticeAndUserRequest } })}
            sx={{
              mt: 2,
              textTransform: "none",
              color: "#2dd4bf",
            }}
          >
            {`Use a different ${twoFaMethod === "email" ? "email" : "mobile#"}`}
          </Button>

          <Typography
            variant="body2"
            textAlign="center"
            mt={3}
            sx={{ color: "#9ca3af" }}
          >
            Two-factor authentication helps protect your account from
            unauthorized access
          </Typography>
        </Card>
      </Box>
    </Box>
  );
}
